import { useState, useEffect, useRef, useCallback } from "react";

/**
 * 数字滚动计数动画 hook
 * 当元素进入视口时，数字从 0 滚动到目标值
 */
function useCountUp(
  targetStr: string,
  duration = 1500,
  threshold = 0.3
): { displayValue: string; ref: React.RefObject<HTMLDivElement | null> } {
  const ref = useRef<HTMLDivElement>(null);
  const [displayValue, setDisplayValue] = useState("0");
  const hasAnimated = useRef(false);

  // 解析目标数值
  const target = parseFloat(targetStr.replace(/,/g, ""));
  const hasComma = targetStr.includes(",");

  const animate = useCallback(() => {
    if (hasAnimated.current) return;
    hasAnimated.current = true;

    const startTime = performance.now();

    function step(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out 缓动
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(target * eased);

      if (hasComma) {
        setDisplayValue(current.toLocaleString());
      } else {
        setDisplayValue(String(current));
      }

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }, [target, duration, hasComma]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          animate();
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [animate, threshold]);

  return { displayValue, ref };
}

export default useCountUp;